
import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useUser } from '@/contexts/UserContext';
import { ShoppingListResponse } from '@/types/shoppingList';
import { CacheService, CacheKeys } from '@/utils/cacheService';


export const useShoppingListPersistence = () => {
  const { user } = useUser();
  const [shoppingList, setShoppingList] = useState<ShoppingListResponse | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const loadSavedShoppingList = async () => {
    if (!user) return;
    
    try {
      // Get the user's current family ID
      const { data: userData } = await supabase
        .from('profiles')
        .select('current_family_id')
        .eq('id', user.id)
        .single();
      
      if (!userData?.current_family_id) {
        console.error('No family ID found for current user');
        return;
      }
      
      // Load from API with cache fallback
      try {
        const { data } = await supabase
          .from('shopping_lists')
          .select('content')
          .eq('family_id', userData.current_family_id)
          .eq('is_active', true)
          .maybeSingle();
        
        if (data?.content) {
          const list = data.content as ShoppingListResponse;
          setShoppingList(list);
          // Cache the successful response
          await CacheService.setCache(
            CacheKeys.shoppingList(userData.current_family_id), 
            list
          );
        }
      } catch (apiError) {
        console.warn('Failed to load from API, trying cache...');
        // Fall back to cache on API error
        const cachedList = await CacheService.getCache<ShoppingListResponse>(
          CacheKeys.shoppingList(userData.current_family_id)
        );
        if (cachedList) {
          setShoppingList(cachedList);
        }
      }
    } catch (err) {
      console.error('Error loading saved shopping list:', err);
    }
  };

  const saveShoppingList = async (list: ShoppingListResponse) => {
    if (!user) return;
    
    try {
      setIsSaving(true);
      
      // Get the user's current family ID
      const { data: userData } = await supabase
        .from('profiles')
        .select('current_family_id')
        .eq('id', user.id)
        .single();
      
      if (!userData?.current_family_id) {
        console.error('No family ID found for current user');
        return;
      }
      
      // Cache the list locally first
      await CacheService.setCache(
        CacheKeys.shoppingList(userData.current_family_id), 
        list
      );
      
      // Save to API
      try {
        // Check if there's already a shopping list for this family
        const { data: existingList } = await supabase
          .from('shopping_lists')
          .select('id')
          .eq('family_id', userData.current_family_id)
          .eq('is_active', true)
          .maybeSingle();
        
        if (existingList) {
          // Update existing shopping list
          await supabase
            .from('shopping_lists')
            .update({ content: list })
            .eq('id', existingList.id);
        } else {
          // Create new shopping list
          await supabase
            .from('shopping_lists')
            .insert({
              family_id: userData.current_family_id,
              content: list,
              is_active: true,
              user_id: user.id
            });
        }
      } catch (apiError) {
        console.warn('Failed to save to API, but cached locally');
        // Don't show error toast since we successfully cached locally
      }
      
    } catch (err) {
      console.error('Error saving shopping list:', err);
      toast.error('Failed to save shopping list');
    } finally {
      setIsSaving(false);
    }
  };

  const handleItemCheckedChange = async (category: string, itemId: string, checked: boolean) => {
    if (!shoppingList) return;
    
    // Create a deep copy of the shopping list
    const updatedShoppingList = JSON.parse(JSON.stringify(shoppingList)) as ShoppingListResponse;
    
    // Find and update the item
    const categoryItems = updatedShoppingList.categories[category];
    const itemIndex = categoryItems.findIndex(item => item.id === itemId);
    
    if (itemIndex > -1) {
      categoryItems[itemIndex].checked = checked;
      setShoppingList(updatedShoppingList);
      
      // Save the updated list to the database
      await saveShoppingList(updatedShoppingList);
    }
  };

  useEffect(() => {
    loadSavedShoppingList();
  }, []);

  return {
    shoppingList,
    isSaving,
    setShoppingList,
    saveShoppingList,
    handleItemCheckedChange,
    loadSavedShoppingList
  };
};
